import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import { type ClientTokenResponse, getTokenFromPrefixedToken, getTokenExpiry, extractPropertyFromBag, TokenResponseType, isTokenFailureDetailsEnabled, } from "owa-tokenprovider-utils";
import addSeconds from "owa-date-utc-fn/lib/addSeconds";
import type TokenResponseCode from "owa-service/lib/contract/TokenResponseCode";

/**
 * Converts a ClientTokenResponse to a TokenResponse format.
 * Extracts the access token and expiry information, and includes additional metadata.
 *
 * @param tokenResponse - The client token response to convert
 * @returns A TokenResponse object
 * @throws Error if the token response is missing required fields or has invalid format
 */
export function tokenResponseToClientTokenResponse(tokenResponse: ClientTokenResponse): TokenResponse {
    // Validate that required fields are present
    if (!tokenResponse?.token || !tokenResponse?.expiresDur) {
        if (isTokenFailureDetailsEnabled() &&
            tokenResponse &&
            tokenResponse.tokenResponseType !== TokenResponseType.Success) {
            return createFailureTokenResponse(tokenResponse);
        }
        throw new Error("Failed to convert ClientTokenResponse: missing token or expiresDur");
    }
    
    const accessToken = getTokenFromPrefixedToken(tokenResponse.token);
    const expiresIn = getTokenExpiry(tokenResponse.token, tokenResponse.expiresDur);
    
    // Validate that conversion succeeded
    if (!accessToken || !expiresIn) {
        throw new Error("Failed to convert ClientTokenResponse: invalid token format or expiry");
    }
    
    const additionalData = new Map<string, string>();
    const retrievalTime = extractPropertyFromBag(tokenResponse, "currentTime");
    const tokenExpiryDate = extractPropertyFromBag(tokenResponse, "tokenExpiryDate");
    
    if (retrievalTime) {
        additionalData.set("retrievalTime", retrievalTime);
    }
    if (tokenExpiryDate) {
        additionalData.set("tokenExpiryDate", tokenExpiryDate);
    }
    
    const response: TokenResponse = {
        AccessToken: accessToken,
        TokenResultCode: 0,
        ExpiresIn: expiresIn, // value in seconds
        AccessTokenExpiry: addSeconds(Date.now(), expiresIn).toISOString(),
        SubErrorCode: tokenResponse?.tokenResponseType,
        AdditionalData: additionalData,
    };
    return response;
}

function createFailureTokenResponse(tokenResponse: ClientTokenResponse): TokenResponse {
    const additionalData = new Map<string, string>();
    const errorStatus = extractPropertyFromBag(tokenResponse, "errorStatus");
    const errorSubStatus = extractPropertyFromBag(tokenResponse, "errorSubStatus");
    const silentAttemptSubStatus = extractPropertyFromBag(tokenResponse, "silentAttemptSubStatus");
    const silentAttemptErrorString = extractPropertyFromBag(tokenResponse, "silentAttemptErrorString");
    
    if (errorStatus) {
        additionalData.set("errorStatus", errorStatus);
    }
    if (errorSubStatus) {
        additionalData.set("errorSubStatus", errorSubStatus);
    }
    if (silentAttemptSubStatus) {
        additionalData.set("silentAttemptSubStatus", silentAttemptSubStatus);
    }
    if (silentAttemptErrorString) {
        additionalData.set("silentAttemptErrorString", silentAttemptErrorString);
    }
    
    return {
        TokenResultCode: 2,
        SubErrorCode: tokenResponse.tokenResponseType,
        ErrorMessage: extractPropertyFromBag(tokenResponse, "errorString"),
        AdditionalData: additionalData.size > 0 ? additionalData : undefined,
    };
}
